import { getLog } from '@aurora/shared-utils/log';

const log = getLog(module);

interface ResolvedSocialFeedSettings {
  enabled: boolean;
  reactionsEnabled: boolean;
  saveFollowEnabled: boolean;
  postReactionTypeIds: string[];
  commentReactionTypeIds: string[];
}

interface SocialFeedBoardSettings {
  configurationType: 'global' | 'custom';
  reactionsEnabled?: boolean;
  saveFollowEnabled?: boolean;
  enabledPostReactionTypeIds?: string[];
  enabledCommentReactionTypeIds?: string[];
}

/** Strip type prefix from stored IDs: "forum:test-forum" → "test-forum" */
function toSlug(id: string): string {
  const i = id.indexOf(':');
  return i >= 0 ? id.slice(i + 1) : id;
}

/** Check if a slug matches any stored ID (which may have a type prefix) */
function slugInList(slug: string, list: string[]): boolean {
  return list.some(id => toSlug(id) === slug);
}

function filterStringIds(ids: unknown): string[] {
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
}

/** Find a board config entry by slug (keys may have type prefixes) */
function findBoardConfig(
  nodeSettings: Record<string, SocialFeedBoardSettings>,
  slug: string
): SocialFeedBoardSettings | null {
  const key = Object.keys(nodeSettings).find(k => toSlug(k) === slug);
  return key ? nodeSettings[key] : null;
}

function parseTargetBoards(json: string | null | undefined): string[] {
  if (!json) {
    return [];
  }
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed?.selectedNodes)) {
      return filterStringIds(parsed.selectedNodes);
    }
    if (Array.isArray(parsed)) {
      return filterStringIds(parsed);
    }
    return [];
  } catch (error) {
    log.warn(
      'Unable to parse social feed targetBoards setting: %s',
      error instanceof Error ? error.message : String(error)
    );
    return [];
  }
}

function parseBoardConfig(
  json: string | null | undefined,
  slug: string
): SocialFeedBoardSettings | null {
  if (!json) {
    return null;
  }
  try {
    const parsed = JSON.parse(json);
    if (parsed?.nodeSettings && typeof parsed.nodeSettings === 'object') {
      return findBoardConfig(parsed.nodeSettings, slug);
    }
    return null;
  } catch (error) {
    log.warn(
      'Unable to parse social feed boardConfig setting: %s',
      error instanceof Error ? error.message : String(error)
    );
    return null;
  }
}

/**
 * Whether social feed is live for at least one board.
 * Cross-board surfaces cannot resolve per board, but they still must not present social feed
 * when the target list is empty - in that state resolveSocialFeedForBoard disables every board.
 *
 * @param socialFeedSettings community social feed settings from AppContext
 */
export function hasSocialFeedTargetBoards(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  socialFeedSettings: any
): boolean {
  return parseTargetBoards(socialFeedSettings?.targetBoards).length > 0;
}

/**
 * Resolves social feed settings for a specific board.
 * boardDisplayId is the board slug (e.g. "test-forum").
 * Empty target boards list means no boards enabled.
 */
export function resolveSocialFeedForBoard(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  socialFeedSettings: any,
  boardDisplayId: string
): ResolvedSocialFeedSettings {
  const disabled: ResolvedSocialFeedSettings = {
    enabled: false,
    reactionsEnabled: false,
    saveFollowEnabled: false,
    postReactionTypeIds: [],
    commentReactionTypeIds: []
  };

  if (!socialFeedSettings?.enabled?.value) {
    return disabled;
  }

  const slug = boardDisplayId;

  const targetBoards = parseTargetBoards(socialFeedSettings.targetBoards);

  if (targetBoards.length === 0 || !slugInList(slug, targetBoards)) {
    return disabled;
  }

  const communityReactionsEnabled = !!socialFeedSettings.reactionsEnabled?.value;
  const communitySaveFollowEnabled = !!socialFeedSettings.saveFollowEnabled?.value;

  const communityPost: string[] = socialFeedSettings.enabledPostReactionTypeIds || [];
  const communityComment: string[] = socialFeedSettings.enabledCommentReactionTypeIds || [];

  const boardConfig = parseBoardConfig(socialFeedSettings.boardConfig, slug);

  let reactionsEnabled: boolean;
  let saveFollowEnabled: boolean;
  let postReactionTypeIds: string[];
  let commentReactionTypeIds: string[];

  if (boardConfig?.configurationType === 'custom') {
    reactionsEnabled = boardConfig.reactionsEnabled ?? communityReactionsEnabled;
    saveFollowEnabled = boardConfig.saveFollowEnabled ?? communitySaveFollowEnabled;
    postReactionTypeIds = boardConfig.enabledPostReactionTypeIds ?? communityPost;
    commentReactionTypeIds = boardConfig.enabledCommentReactionTypeIds ?? communityComment;
  } else {
    reactionsEnabled = communityReactionsEnabled;
    saveFollowEnabled = communitySaveFollowEnabled;

    postReactionTypeIds = communityPost;
    commentReactionTypeIds = communityComment;
  }

  return {
    enabled: true,
    reactionsEnabled,
    saveFollowEnabled,
    postReactionTypeIds,
    commentReactionTypeIds
  };
}
